import { JSX, useMemo } from "react";

import { BarChartOutlined, InsertDriveFileOutlined } from "@mui/icons-material";
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Theme, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocationsDataQuery } from "src/api/locationQueries.api";
import Pages from "src/constants/pages.enum";
import { UserAppRole } from "src/constants/userRoles.enum";
import { getAvailablePagesBasedOnLocationStructure } from "src/lib/locationTree.helper";
import { getAppRolesFromUser } from "src/lib/user.helper";
import LocationType from "src/types/LocationType";

import SidebarHeader from "./components/SidebarHeader";
import SidebarLocationTypeList from "./components/SidebarLocationTypeList";
import SidebarPinnedList from "./components/SidebarPinnedList";
import { getStyles } from "./sidebar.helpers";

export const styles = (theme: Theme) => ({
  toolbar: {
    border: "none",
    display: "flex",
    px: 20,
    py: 20,
    alignItems: "center",
    height: 72,
  },
  list: { pb: 2 },
  drawerBox: { display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100vh" },
  pagesList: (open: boolean) => ({ mt: open ? 1 : 4 }),
  scrollBox: {
    overflowY: "auto",
    maxHeight: { xs: "calc(100vh - 100px)", sm: "calc(100vh - 50px)" },
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.transparent.pure,
      borderRadius: theme.radius.sm,
    },
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": {
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.transparent.heavy,
      },
      scrollbarColor: `${theme.palette.transparent.heavy} transparent`,
    },
  },
});

function SidebarContent({ open }: { open: boolean }): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: locations } = useLocationsDataQuery();

  const userAppRoles = useMemo(() => getAppRolesFromUser(auth.user), [auth.user]);
  const availablePages: Pages[] = useMemo(
    () => (locations?.value ? getAvailablePagesBasedOnLocationStructure(locations.value, userAppRoles) : []),
    [locations?.value, userAppRoles]
  );

  return (
    <Box sx={styles(theme).drawerBox} overflow={"hidden"}>
      <Box>
        <SidebarHeader open={open} />

        <Box sx={styles(theme).scrollBox}>
          <Box sx={styles(theme).pagesList(open)}>
            {userAppRoles.includes(UserAppRole.MallViewer) && (
              <SidebarLocationTypeList
                open={open}
                locationType={LocationType.Mall}
                parentEnabled={availablePages.includes(Pages.CenterRegionDashboard)}
                childsEnabled={availablePages.includes(Pages.CenterDashboard)}
              />
            )}

            {userAppRoles.includes(UserAppRole.ShopViewer) && (
              <SidebarLocationTypeList
                open={open}
                locationType={LocationType.Shop}
                parentEnabled={availablePages.includes(Pages.ShopRegionDashboard)}
                childsEnabled={availablePages.includes(Pages.ShopDashboard)}
              />
            )}

            {userAppRoles.includes(UserAppRole.StationViewer) && (
              <SidebarLocationTypeList
                open={open}
                locationType={LocationType.Station}
                parentEnabled={availablePages.includes(Pages.StationRegionDashboard)}
                childsEnabled={availablePages.includes(Pages.StationDashboard)}
              />
            )}
            {(userAppRoles.includes(UserAppRole.ShopViewer) ||
              userAppRoles.includes(UserAppRole.MallViewer) ||
              userAppRoles.includes(UserAppRole.StationViewer)) && <SidebarPinnedList open={open} />}

            <List sx={styles(theme).list}>
              {availablePages.includes(Pages.InsightsPage) && (
                <ListItem>
                  <ListItemButton
                    sx={getStyles(theme).listItemButton(open, true)}
                    onClick={() => navigate("/extended-analytics")}
                    selected={pathname.includes("extended-analytics")}
                  >
                    <ListItemIcon sx={getStyles(theme).listItemIcon(open)}>
                      <BarChartOutlined />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={getStyles(theme).listItemPrimaryTypographyProps}
                      primary={t("insights")}
                      sx={getStyles(theme).listItemText(open)}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {availablePages.includes(Pages.DocumentPage) && (
                <ListItem>
                  <ListItemButton
                    sx={getStyles(theme).listItemButton(open, true)}
                    onClick={() => navigate("/files", { replace: true })}
                    selected={pathname.includes("files")}
                  >
                    <ListItemIcon sx={getStyles(theme).listItemIcon(open)}>
                      <InsertDriveFileOutlined />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={getStyles(theme).listItemPrimaryTypographyProps}
                      primary={t("files")}
                      sx={getStyles(theme).listItemText(open)}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SidebarContent;
